import React from 'react';
import { Box, Card, CardContent, Grid, Typography, Skeleton } from '@mui/material';
import ForumIcon from '@mui/icons-material/Forum';
import SpeedIcon from '@mui/icons-material/Speed';
import GroupIcon from '@mui/icons-material/Group';
import TollIcon from '@mui/icons-material/Toll';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

interface AiUsageSummaryCardsProps {
    totalRequests: number;
    totalTokens: number;
    promptTokens: number;
    completionTokens: number;
    averageDurationMs: number;
    activeProviders: number;
    totalCostUsd?: number;
    projectedCostUsd?: number;
    currentMonthProjectedCostUsd?: number;
    loading: boolean;
}

const AiUsageSummaryCards: React.FC<AiUsageSummaryCardsProps> = ({
    totalRequests,
    totalTokens,
    promptTokens,
    completionTokens,
    averageDurationMs,
    activeProviders,
    totalCostUsd = 0,
    projectedCostUsd = 0,
    currentMonthProjectedCostUsd = 0,
    loading,
}) => {
    const cards = [
        {
            title: 'Estimated Cost',
            value: `$${totalCostUsd.toFixed(4)}`,
            subtitle: 'Calculated via token usage',
            icon: <AttachMoneyIcon sx={{ fontSize: 32, opacity: 0.8 }} />,
            gradient: 'linear-gradient(135deg, #009688 0%, #00e676 100%)',
            shadow: '0 8px 24px rgba(0, 150, 136, 0.25)',
        },
        {
            title: 'Cost Projection (EOM)',
            value: `$${currentMonthProjectedCostUsd.toFixed(4)}`,
            subtitle: 'Projected monthly cost',
            icon: <TrendingUpIcon sx={{ fontSize: 32, opacity: 0.8 }} />,
            gradient: 'linear-gradient(135deg, #7b1fa2 0%, #e040fb 100%)',
            shadow: '0 8px 24px rgba(123, 31, 162, 0.25)',
        },
        {
            title: 'Total Requests',
            value: totalRequests.toLocaleString(),
            subtitle: 'AI assistant queries',
            icon: <ForumIcon sx={{ fontSize: 32, opacity: 0.8 }} />,
            gradient: 'linear-gradient(135deg, #3f51b5 0%, #00bcd4 100%)',
            shadow: '0 8px 24px rgba(63, 81, 181, 0.25)',
        },
        {
            title: 'Tokens Consumed',
            value: totalTokens.toLocaleString(),
            subtitle: `${promptTokens.toLocaleString()} prompt / ${completionTokens.toLocaleString()} completion`,
            icon: <TollIcon sx={{ fontSize: 32, opacity: 0.8 }} />,
            gradient: 'linear-gradient(135deg, #e91e63 0%, #ff80ab 100%)',
            shadow: '0 8px 24px rgba(233, 30, 99, 0.25)',
        },
        {
            title: 'Average Latency',
            value: `${(averageDurationMs / 1000).toFixed(2)}s`,
            subtitle: 'Avg response speed',
            icon: <SpeedIcon sx={{ fontSize: 32, opacity: 0.8 }} />,
            gradient: 'linear-gradient(135deg, #4caf50 0%, #8bc34a 100%)',
            shadow: '0 8px 24px rgba(76, 175, 80, 0.25)',
        },
        {
            title: 'Active Providers',
            value: activeProviders.toLocaleString(),
            subtitle: 'Unique users using AI',
            icon: <GroupIcon sx={{ fontSize: 32, opacity: 0.8 }} />,
            gradient: 'linear-gradient(135deg, #ff9800 0%, #ffc107 100%)',
            shadow: '0 8px 24px rgba(255, 152, 0, 0.25)',
        },
    ];

    return (
        <Grid container spacing={3}>
            {cards.map((card, index) => (
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }} key={index}>
                    <Card
                        elevation={3}
                        sx={{
                            background: card.gradient,
                            color: '#ffffff',
                            borderRadius: 3,
                            boxShadow: card.shadow,
                            position: 'relative',
                            overflow: 'hidden',
                            transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: card.shadow.replace('0.25', '0.4'),
                            },
                        }}
                    >
                        {/* Decorative background circle */}
                        <Box
                            sx={{
                                position: 'absolute',
                                right: -20,
                                top: -20,
                                width: 100,
                                height: 100,
                                borderRadius: '50%',
                                background: 'rgba(255, 255, 255, 0.1)',
                                pointerEvents: 'none',
                            }}
                        />
                        <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                <Typography variant="subtitle2" sx={{ opacity: 0.9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                    {card.title}
                                </Typography>
                                {card.icon}
                            </Box>
                            {loading ? (
                                <Box>
                                    <Skeleton variant="text" width="60%" height={40} sx={{ bgcolor: 'rgba(255, 255, 255, 0.3)' }} />
                                    <Skeleton variant="text" width="80%" sx={{ bgcolor: 'rgba(255, 255, 255, 0.3)' }} />
                                </Box>
                            ) : (
                                <Box>
                                    <Typography variant="h4" fontWeight="700" sx={{ mb: 0.5 }}>
                                        {card.value}
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.8, fontSize: '0.825rem' }}>
                                        {card.subtitle}
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            ))}
        </Grid>
    );
};

export default AiUsageSummaryCards;
